// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { enableMapSet, produce } from 'immer';
import { useMemo } from 'react';
import { type PortfolioPerformanceData } from '@shared/data-access/interfaces/api/brokerage/portfolio';

enableMapSet();

/**
 * The grouped data structure, where data is grouped by year and month, and only the
 * last day of each month is retained.
 */
export interface PortfolioPerformanceGrouped {
  [year: string]: { [month: string]: PortfolioPerformanceData };
}

const initializeYear = (year: number): Record<number, PortfolioPerformanceData> =>
  Object.fromEntries(
    Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;

      return [
        month,
        {
          portfolio_return: NaN,
          outstanding_balance: NaN,
          date: `${month}/01/${year}`,
        },
      ];
    }),
  );

/**
 * Custom React hook to group portfolio performance data by year and month,
 * retaining only the last day of each month.
 *
 * @example // Import the custom hook import { usePerformanceTable } from
 * './usePerformanceTable'
 *
 * // Sample data of portfolio performance const performanceData:
 * PortfolioPerformanceData[] = [ // ... (your portfolio performance data here) ]
 *
 * // Use the custom hook to group the data const { performance } =
 * usePerformanceTable(performanceData)
 *
 * // Access grouped data, for example, to get all data for August 2023 const
 * august2023Data = performance['2023']['08'] // Contains only the last day data for
 * August 2023
 *
 * @param data - The array of portfolio performance data to be grouped.
 */
export const usePerformanceTable = (
  data: PortfolioPerformanceData[],
): {
  performance: PortfolioPerformanceGrouped;
} => {
  const performanceMap = useMemo(
    () =>
      data.reduce((group, performance) => {
        const date = new Date(performance.date);

        if (isNaN(date.getTime())) {
          return group;
        }

        const year = date.getFullYear();

        const month = date.getMonth() + 1;

        return produce(group, draft => {
          // Initialize the year if it doesn't exist
          if (!draft.has(year)) draft.set(year, initializeYear(year));

          // Append the item to the corresponding month
          draft.get(year)![month] = performance;
        });
      }, new Map<number, { [month: number]: PortfolioPerformanceData }>()),
    [data],
  );

  const performance: PortfolioPerformanceGrouped = useMemo(
    () => Object.fromEntries(performanceMap.entries()),
    [performanceMap],
  );

  return {
    performance,
  };
};
